/**
 * Manages the WebSocket session lifecycle, video recording, and UI state
 * for the call screen. Coordinates between the audio pipeline and countdown
 * hooks via destructured stable references.
 *
 * IMPORTANT: Hook object params (audioPipeline, countdowns) are destructured
 * into individual callbacks at the top of the hook body. This prevents
 * unstable object identities from invalidating useCallback dependency arrays,
 * which previously caused WebSocket reconnect loops on every render.
 * See the destructuring block below for details.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { getPostHogIds, captureEvent, captureException } from '../../../lib/telemetry/posthog/frontend';
import {
  buildLiveInterviewBaseProps,
  classifyLiveInterviewAbandoned,
} from '../../../lib/telemetry/live-interview-contract';
import { getMediaStreamWithFallback } from '../../../lib/media-utils';
import { liveSessionWorkerUrl } from '../../../lib/worker-urls';
import { createVideoRecorder } from '../../../services/videoRecorderService';
import { ScreenShareService } from '../../../services/screenShareService';
import { routeMessage, type MessageHandlers } from '../message-router';
import { useInformationBlockMount } from './useInformationBlockMount';
import { SessionConnection, ConnectionState } from '../session-connection';
import type { RespondentConversationState } from '../../../lib/multimodal-contract';

export { ConnectionState } from '../session-connection';
import type { ConversationTurn, ResearchEvent, InterviewContext } from '../../../types';
import type { VideoRecorder } from '../../../services/videoRecorderService';
import type { RealtimeServerMessage, ServerErrorCode } from '../protocol-types';

const LIVE_SESSION_WORKER_URL = liveSessionWorkerUrl();

/**
 * Shape of the JSON returned by the live-session worker's
 * `GET /check-occupancy` endpoint. Used to decide whether a room is already
 * open elsewhere before connecting.
 */
interface OccupancyCheckResponse {
  occupied?: boolean;
  message?: string;
}

function safeServerErrorMessage(code: ServerErrorCode): string {
  switch (code) {
    case 'conversation_unavailable':
      return 'This conversation cannot start.';
    case 'service_unavailable':
      return 'Service temporarily unavailable. Please try again later.';
    case 'realtime_unavailable':
      return 'We could not start the conversation. Please try again.';
  }
  return 'Service temporarily unavailable. Please try again later.';
}

interface UseSessionWebSocketOptions {
  wsRef: React.MutableRefObject<WebSocket | null>;
  wsReadyRef: React.MutableRefObject<boolean>;
  audioPipeline: {
    setupAudioPipeline: () => Promise<{ analyserNode: AnalyserNode; stream: MediaStream }>;
    playAudioChunk: (
      message: RealtimeServerMessage,
      analyserNode: AnalyserNode | null,
      currentStatus: string,
      setStatus: (s: string | ((prev: string) => string)) => void,
      setConversation: React.Dispatch<React.SetStateAction<ConversationTurn[]>>,
      setQuestionsComplete: (v: boolean) => void,
      onPerformResearch: (query: string, type: 'primary' | 'background') => void,
    ) => Promise<void>;
    handleTrackEnded: (
      hasEndedRef: React.MutableRefObject<boolean>,
      connectedRef: React.MutableRefObject<boolean>,
      setStatus: (s: string | ((prev: string) => string)) => void,
    ) => void;
    handleTrackEndedRef: React.MutableRefObject<(() => void) | null>;
    cleanupAudio: () => void;
    sendToWorker: (data: unknown) => void;
    confirmEndingAfterPlayback: () => void;
    markAiIntroStarted: () => void;
    armFirstUtteranceProtection: () => void;
    setRealtimeAudioPolicy: (sampleRateHz: number, chunkDurationMs: number) => void;
    recordingAudioDestRef: React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
    mediaStreamRef: React.MutableRefObject<MediaStream | null>;
  };
  countdowns: {
    startEndingCountdown: (seconds: number) => void;
    cancelEndingCountdown: () => void;
    startInactivityCountdown: (seconds: number) => void;
    cancelInactivityCountdown: () => void;
    clearEndingCountdown: () => void;
    cleanupCountdowns: () => void;
  };
  screenShare: {
    requestFromAi: (reason: string) => void;
    onEndRequestedFromAi: (reason: string) => void;
    cleanup: () => void;
  };
  props: {
    onEndCall: (conversation: ConversationTurn[], researchEvents: ResearchEvent[]) => void;
    onPerformResearch: (query: string, type: 'primary' | 'background', existingSummary?: string) => Promise<string>;
    researchEvents: ResearchEvent[];
    context: InterviewContext;
    videoEnabled: boolean;
    userCameraEnabled?: boolean;
    preferredCameraId?: string;
    roomId: string;
    passcode: string;
    interviewDataId?: string;
    enforceLanguage?: string | null;
    responseToken?: string;
    onConversationState?: (state: RespondentConversationState) => void;
  };
}

export function useSessionWebSocket({
  wsRef,
  wsReadyRef,
  audioPipeline,
  countdowns,
  screenShare,
  props,
}: UseSessionWebSocketOptions) {
  const { onEndCall, onPerformResearch, researchEvents, context, videoEnabled, userCameraEnabled = true, preferredCameraId, roomId, passcode, interviewDataId, enforceLanguage, responseToken, onConversationState } = props;
  const conversationStateHandler = useRef(onConversationState);
  conversationStateHandler.current = onConversationState;
  /** Used for worker URL and occupancy checks only; analytics `flow_id` is always `roomId`. */
  const sessionIdentityId = interviewDataId && interviewDataId.trim().length > 0 ? interviewDataId : roomId;
  const trackLiveInterviewEvent = useCallback(
    (eventName: string, step: 'in_call' | 'ending', properties?: Record<string, string>) => {
      captureEvent(eventName, {
        ...buildLiveInterviewBaseProps(roomId, step, interviewDataId),
        ...(properties || {}),
      });
    },
    [roomId, interviewDataId],
  );

  // BUG FIX: Destructure stable callbacks from hook objects up front.
  // React hook return objects get a new identity every render. If we used
  // `audioPipeline.setupAudioPipeline` directly in a useCallback dep array,
  // the callback would regenerate every render, causing the session-start
  // useEffect to re-fire and reconnect the WebSocket in an infinite loop.
  // Destructured function references are stable (they come from useCallback).
  // Refs are also stable by identity and accessed via .current at call time.
  const {
    setupAudioPipeline,
    playAudioChunk,
    cleanupAudio,
    sendToWorker,
    confirmEndingAfterPlayback,
    markAiIntroStarted,
    armFirstUtteranceProtection,
    setRealtimeAudioPolicy,
    recordingAudioDestRef: audioRecordingDestRef,
    mediaStreamRef: audioMediaStreamRef,
    handleTrackEndedRef,
    handleTrackEnded,
  } = audioPipeline;

  const {
    startInactivityCountdown,
    cancelInactivityCountdown,
    clearEndingCountdown,
    cleanupCountdowns,
  } = countdowns;

  // Stabilize screen-share callbacks the same way audio/countdown callbacks are stabilized
  // (see BUG FIX note above).
  const {
    requestFromAi: screenShareRequestFromAi,
    onEndRequestedFromAi: screenShareOnEndRequestedFromAi,
    cleanup: screenShareCleanup,
  } = screenShare;

  // Single currently-mounted information block (insight D). The DO is the
  // authority; this only reflects mount/unmount messages and never sends one.
  const {
    block: mountedInformationBlock,
    mount: mountInformationBlock,
    unmount: unmountInformationBlock,
  } = useInformationBlockMount();

  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [status, setStatus] = useState('Connecting...');
  // Explicit connection state, kept distinct from the presentational `status`
  // text so consumers can key connection logic off the enum instead of magic
  // strings. Mirrors the SessionConnection's own state.
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Idle);
  const [showEndModal, setShowEndModal] = useState(false);
  const [questionsComplete, setQuestionsComplete] = useState(false);
  const [hasConnectionError, setHasConnectionError] = useState(false);
  const [connectionErrorMessage, setConnectionErrorMessage] = useState<string>('');
  const [connectionErrorCode, setConnectionErrorCode] = useState<ServerErrorCode | null>(null);
  const [connectionErrorRetryable, setConnectionErrorRetryable] = useState(true);
  const [isVideoRecording, setIsVideoRecording] = useState(false);
  const [videoRecordingWarning, setVideoRecordingWarning] = useState<string | null>(null);
  const [isVideoPreviewMinimized, setIsVideoPreviewMinimized] = useState(() => {
    const saved = localStorage.getItem('holito_video_preview_minimized');
    return saved === 'true';
  });
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportIssueText, setReportIssueText] = useState('');
  const [isNudging, setIsNudging] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(!userCameraEnabled);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);

  const hasEndedRef = useRef(false);
  const connectedRef = useRef(false);
  // Owns the WebSocket, auth handshake, and reconnect state machine. The hook
  // is a thin adapter: it supplies the React-coupled message handlers and maps
  // the connection's state into this hook's return shape. Its internal cancel
  // flag (checked after every reconnect backoff hop) is what prevents an
  // unmount mid-backoff from spawning a zombie socket that re-occupies the room.
  const sessionConnectionRef = useRef<SessionConnection | null>(null);
  const sessionCallStartedAtRef = useRef<number | null>(null);
  const firstUserTurnEmittedRef = useRef(false);
  const abandonedEmittedRef = useRef(false);
  const wasOfflineDuringCallRef = useRef(false);
  const fatalConnectionErrorShownRef = useRef(false);
  const notifiedResearchIds = useRef<Set<string>>(new Set());
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const videoRecorderRef = useRef<VideoRecorder | null>(null);
  const reportMenuRef = useRef<HTMLDivElement>(null);

  // Snapshot refs -- allow handleEndCallAndCleanup to read latest state
  // without adding conversation/researchEvents to its deps.
  const finalConversationRef = useRef(conversation);
  const finalResearchEventsRef = useRef(researchEvents);

  useEffect(() => { finalConversationRef.current = conversation; }, [conversation]);

  useEffect(() => {
    if (firstUserTurnEmittedRef.current) return;
    const hasUserTurn = conversation.some((t) => t.speaker === 'user');
    if (!hasUserTurn) return;
    firstUserTurnEmittedRef.current = true;
    trackLiveInterviewEvent('live_interview.first_user_turn', 'in_call');
  }, [conversation, trackLiveInterviewEvent]);
  useEffect(() => { finalResearchEventsRef.current = researchEvents; }, [researchEvents]);

  useEffect(() => {
    return () => {
      cleanupCountdowns();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flush any buffered video data before the tab closes
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (videoRecorderRef.current?.isActive()) {
        videoRecorderRef.current.requestData();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const onOffline = () => {
      wasOfflineDuringCallRef.current = true;
    };
    const onOnline = () => {
      wasOfflineDuringCallRef.current = false;
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  useEffect(() => {
    const onPageHide = (e: PageTransitionEvent) => {
      if (hasEndedRef.current || abandonedEmittedRef.current) return;
      if (e.persisted) return;
      if (sessionCallStartedAtRef.current == null) return;
      abandonedEmittedRef.current = true;
      const duration = Date.now() - sessionCallStartedAtRef.current;
      const ws = wsRef.current;
      const clientSocketClosed = !ws || ws.readyState === WebSocket.CLOSED;
      const reason = classifyLiveInterviewAbandoned(e, {
        wasOffline: wasOfflineDuringCallRef.current,
        clientSocketClosed,
      });
      captureEvent('live_interview.abandoned', {
        ...buildLiveInterviewBaseProps(roomId, 'in_call', interviewDataId),
        reason,
        duration_ms: duration,
      });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [roomId, interviewDataId, wsRef]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (reportMenuRef.current && !reportMenuRef.current.contains(event.target as Node)) {
      setShowReportMenu((previous) => {
        if (previous) {
          trackLiveInterviewEvent('live_interview.control_more_menu_toggled', 'in_call', { state: 'closed' });
        }
        return false;
      });
    }
  }, [trackLiveInterviewEvent]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (!videoEnabled || !userCameraEnabled) return undefined;

    const startVideoPreview = async () => {
      try {
        const result = await getMediaStreamWithFallback({
          deviceId: preferredCameraId || undefined,
          kind: 'video',
          constraints: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          context: 'video_preview',
        });
        videoStreamRef.current = result.stream;
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = result.stream;
        }
      } catch (error) {
        console.error('[Video] Failed to start video preview:', error);
        captureException(error instanceof Error ? error : String(error), 'useSessionWebSocket.initVideoPreview', { preferredCameraId });
        // Mark camera as paused so the toggle button can re-attempt acquisition
        setIsVideoPaused(true);
      }
    };
    void startVideoPreview();
    return () => {
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
        videoStreamRef.current = null;
      }
    };
  }, [videoEnabled, userCameraEnabled, preferredCameraId]);

  useEffect(() => {
    localStorage.setItem('holito_video_preview_minimized', String(isVideoPreviewMinimized));
  }, [isVideoPreviewMinimized]);

  // Forward newly completed research summaries to the AI session so the
  // interviewer can incorporate findings into follow-up questions.
  useEffect(() => {
    researchEvents.forEach(event => {
      if (event.status === 'complete' && !notifiedResearchIds.current.has(event.id)) {
        notifiedResearchIds.current.add(event.id);
        if (wsRef.current && wsReadyRef.current) {
          wsRef.current.send(JSON.stringify({
            type: 'text',
            text: `[SYSTEM NOTIFICATION] Research completed for query: "${event.query}". Summary: ${event.summary}`,
          }));
        }
      }
    });
  }, [researchEvents, wsRef, wsReadyRef]);

  /**
   * Tear down all session resources: video recorder, WebSocket, and audio pipeline.
   * Recorder teardown is async (it awaits the final MediaRecorder flush plus any
   * buffered chunks), so the socket close, and the optional 'end' message that
   * makes the worker finalize the video segment, are deferred until that flush
   * lands. The refs are detached synchronously so handlers ignore the old socket.
   */
  const cleanupResources = useCallback((options?: {
    sendEndMessage?: boolean;
    cleanClose?: boolean;
  }) => {
    const recorder = videoRecorderRef.current;
    videoRecorderRef.current = null;

    // Stop every acquired camera track regardless of join-time camera state.
    // When the user joined camera-off the preview effect early-returns and never
    // registers its own cleanup, so a camera acquired via the mid-call toggle
    // would otherwise leak (the indicator light stays on after teardown).
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((track) => track.stop());
      videoStreamRef.current = null;
    }

    const ws = wsRef.current;
    wsRef.current = null;
    wsReadyRef.current = false;

    // Cancel the connection's reconnect machine and detach its socket handlers
    // so the close we are about to perform does not bounce back into a reconnect.
    sessionConnectionRef.current?.cancel();
    sessionConnectionRef.current = null;

    const finishSocket = () => {
      if (!ws) return;
      if (options?.sendEndMessage && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: responseToken ? 'call_leave' : 'end' })); } catch (_e) { /* ignore */ }
      }
      try {
        if (options?.cleanClose) {
          ws.close(1000, 'Interview ended');
        } else {
          ws.close();
        }
      } catch (_e) { /* ignore */ }
    };

    if (recorder) {
      setIsVideoRecording(false);
      Promise.resolve(recorder.cleanup()).then(finishSocket, finishSocket);
    } else {
      finishSocket();
    }

    screenShareCleanup();
    cleanupAudio();
    connectedRef.current = false;
  }, [wsRef, wsReadyRef, cleanupAudio, screenShareCleanup, responseToken]);

  /** Show one fatal connection error and stop all session resources immediately. */
  const showFatalConnectionError = useCallback((
    message: string,
    code: ServerErrorCode | null = null,
    retryable = true,
  ) => {
    if (hasEndedRef.current || fatalConnectionErrorShownRef.current) return;
    fatalConnectionErrorShownRef.current = true;
    // Ensure no countdown modal can outlive a fatal connection error state.
    clearEndingCountdown();
    cancelInactivityCountdown();
    cleanupCountdowns();
    setConnectionErrorMessage(message);
    setConnectionErrorCode(code);
    setConnectionErrorRetryable(retryable);
    setHasConnectionError(true);
    setStatus(message);
    cleanupResources();
  }, [clearEndingCountdown, cancelInactivityCountdown, cleanupCountdowns, cleanupResources]);

  /** Clean up an intentional ending and hand conversation data back to parent. */
  const handleEndCallAndCleanup = useCallback((sendEndMessage = true) => {
    if (hasEndedRef.current) return;
    hasEndedRef.current = true;

    cleanupResources({ sendEndMessage, cleanClose: true });
    onEndCall(finalConversationRef.current, finalResearchEventsRef.current);
  }, [onEndCall, cleanupResources]);

  const requestEndInterview = useCallback(() => {
    trackLiveInterviewEvent('live_interview.control_end_clicked', 'ending');
    setShowEndModal(true);
  }, [trackLiveInterviewEvent]);
  const handleConfirmEnd = useCallback(() => {
    trackLiveInterviewEvent('live_interview.control_end_confirmed', 'ending');
    setShowEndModal(false);
    handleEndCallAndCleanup();
  }, [handleEndCallAndCleanup, trackLiveInterviewEvent]);
  const handleCancelEnd = useCallback(() => {
    trackLiveInterviewEvent('live_interview.control_end_cancelled', 'ending');
    setShowEndModal(false);
  }, [trackLiveInterviewEvent]);

  const handleNudgeAi = useCallback(() => {
    if (isNudging) return;
    trackLiveInterviewEvent('live_interview.control_nudge_clicked', 'in_call');
    setIsNudging(true);
    sendToWorker({ type: 'nudge_ai' });
    setShowReportMenu((previous) => {
      if (previous) {
        trackLiveInterviewEvent('live_interview.control_more_menu_toggled', 'in_call', { state: 'closed' });
      }
      return false;
    });
    setStatus('Nudging AI...');
    setTimeout(() => {
      setStatus(prev => prev === 'Nudging AI...' ? 'Listening...' : prev);
      setIsNudging(false);
    }, 5000);
  }, [isNudging, sendToWorker, trackLiveInterviewEvent]);

  const handleReportIssue = useCallback(() => {
    if (!reportIssueText.trim()) return;
    sendToWorker({ type: 'report_issue', description: reportIssueText });
    setReportIssueText('');
    setShowReportModal(false);
    trackLiveInterviewEvent('live_interview.control_report_issue_submitted', 'in_call');
    setStatus('Issue reported');
    setTimeout(() => setStatus('Listening...'), 2000);
  }, [reportIssueText, sendToWorker, trackLiveInterviewEvent]);

  /**
   * Start the call recorder. For video calls (videoEnabled=true) this captures
   * camera + the mixed mic/AI audio. For audio-only calls (videoEnabled=false)
   * it captures the mixed audio alone - the worker side stores it with
   * segment_kind='audio' so the admin can listen back.
   */
  const startVideoRecordingIfReady = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // Mid-call reconnect: the recorder survives but holds the dead socket.
    // Rebind it to the new socket so chunk uploads resume; recreating the
    // recorder would reset chunk indexes and overwrite earlier chunks.
    if (videoRecorderRef.current) {
      videoRecorderRef.current.setWebSocket(wsRef.current);
      return;
    }

    // Video call: wait for the camera stream before starting; the camera-toggle
    // path will call us again once the preview lands. Skipping here without
    // returning would record an audio-only segment for a video call.
    if (videoEnabled && !videoStreamRef.current) return;

    const mixedAudioStream = audioRecordingDestRef.current?.stream || audioMediaStreamRef.current || undefined;

    try {
      const recorder = createVideoRecorder();
      videoRecorderRef.current = recorder;
      if (audioRecordingDestRef.current) {
        console.log('[Recorder] Using mixed audio stream (mic + AI voice)');
      } else {
        console.warn('[Recorder] Recording destination not ready, falling back to mic only');
      }
      recorder.start({
        videoStream: videoEnabled ? videoStreamRef.current ?? undefined : undefined,
        audioStream: mixedAudioStream,
        websocket: wsRef.current,
        onError: (error: Error) => {
          console.error('[Recorder] Recording error:', error);
          setIsVideoRecording(false);
        },
        onChunkSent: (chunkIndex: number, size: number) => {
          if (chunkIndex % 10 === 0) console.log(`[Recorder] Sent chunk ${chunkIndex} (${Math.round(size / 1024)}KB)`);
        },
        onRecordingStarted: (kind, clientStartMs) => {
          setIsVideoRecording(true);
          // Report the capture-time anchor so the worker can stamp the segment's
          // client_start_ms. Best-effort: dropped if the connection is not up.
          sessionConnectionRef.current?.send({ type: 'recording_started', kind, client_start_ms: clientStartMs });
        },
        onRecordingStopped: () => {
          setIsVideoRecording(false);
        },
      });
    } catch (error) {
      console.error('[Recorder] Failed to start recording:', error);
      captureException(error instanceof Error ? error : String(error), 'useSessionWebSocket.startVideoRecording', { roomId, videoEnabled });
    }
  }, [videoEnabled, wsRef, audioRecordingDestRef, audioMediaStreamRef, roomId]);

  /**
   * Toggle the camera on/off mid-call.
   * - If stream exists: toggles videoTrack.enabled (recording continues with black frames).
   * - If no stream: acquires camera on-demand, sets up preview, and starts recording.
   */
  const toggleVideoPause = useCallback(async () => {
    // Turning camera OFF - disable existing tracks
    if (!isVideoPaused && videoStreamRef.current) {
      const tracks = videoStreamRef.current.getVideoTracks();
      tracks.forEach(track => { track.enabled = false; });
      setIsVideoPaused(true);
      return;
    }

    // Turning camera ON - re-enable existing tracks
    if (isVideoPaused && videoStreamRef.current) {
      const tracks = videoStreamRef.current.getVideoTracks();
      tracks.forEach(track => { track.enabled = true; });
      setIsVideoPaused(false);
      return;
    }

    // Turning camera ON with no stream - acquire camera on demand
    if (isVideoPaused && !videoStreamRef.current) {
      try {
        const result = await getMediaStreamWithFallback({
          deviceId: preferredCameraId || undefined,
          kind: 'video',
          constraints: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          context: 'video_toggle_on',
        });
        videoStreamRef.current = result.stream;
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = result.stream;
        }
        setIsVideoPaused(false);
        startVideoRecordingIfReady();
      } catch (error) {
        console.error('[Video] Failed to acquire camera on toggle:', error);
        captureException(error instanceof Error ? error : String(error), 'useSessionWebSocket.toggleVideoPause', { roomId });
        // Stay paused - acquisition failed
      }
    }
  }, [isVideoPaused, preferredCameraId, roomId, startVideoRecordingIfReady]);

  // Bridge: inject session-owned refs into the audio pipeline's track-ended handler
  useEffect(() => {
    handleTrackEndedRef.current = () => {
      handleTrackEnded(hasEndedRef, connectedRef, setStatus);
    };
  }, [handleTrackEnded, handleTrackEndedRef]);

  /**
   * Initialize the full session: check room occupancy, set up audio pipeline,
   * open WebSocket to live-session worker, and wire all message handlers.
   * Guarded by connectedRef to prevent duplicate connections.
   */
  const startSession = useCallback(async (signal?: AbortSignal) => {
    if (connectedRef.current) return;

    if (!roomId || (!passcode && !interviewDataId)) {
      setStatus("Error: Missing room credentials.");
      console.error('roomId and either passcode or interviewDataId are required');
      return;
    }

    // New session attempt starts with a clean fatal-error gate.
    fatalConnectionErrorShownRef.current = false;
    setConnectionErrorCode(null);
    setConnectionErrorRetryable(true);

    try {
      setStatus('Checking room availability...');
      const checkUrl = `${LIVE_SESSION_WORKER_URL}/check-occupancy?interview_data_id=${encodeURIComponent(sessionIdentityId)}`;
      const checkResponse = await fetch(checkUrl, { signal });

      if (signal?.aborted) return;

      if (checkResponse.ok) {
        const checkData = await checkResponse.json() as OccupancyCheckResponse;
        if (checkData.occupied) {
          const errorMsg = checkData.message || 'This interview is already open elsewhere.';
          setConnectionErrorMessage(errorMsg);
          setHasConnectionError(true);
          setStatus(errorMsg);
          return;
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Error checking room occupancy:', err);
      captureException(err instanceof Error ? err : String(err), 'useSessionWebSocket.checkRoomOccupancy', { roomId });
    }

    if (signal?.aborted) return;

    connectedRef.current = true;

    try {
      setStatus('Requesting microphone access...');

      const { analyserNode } = await setupAudioPipeline();

      if (signal?.aborted) {
        connectedRef.current = false;
        cleanupAudio();
        return;
      }

      setStatus('Listening...');

      const interviewContext = {
        targetName: context.targetName,
        targetUrl: context.targetUrl,
        goal: context.goal,
        details: context.details,
        productResearch: context.productResearch,
        intervieweeName: context.intervieweeName,
        intervieweeJob: context.intervieweeJob,
        intervieweeCompany: context.intervieweeCompany,
        intervieweeLinkedin: context.intervieweeLinkedin,
        intervieweeCompanyUrl: context.intervieweeCompanyUrl,
        interviewLanguage: context.interviewLanguage,
        outputLanguage: context.outputLanguage,
        // Platform capability - worker ANDs this with the campaign DB flag
        // before declaring screen-share tools upstream. False on iOS /
        // Android where getDisplayMedia can't succeed.
        clientSupportsScreenShare: ScreenShareService.isSupported(),
        // Browser timezone for calendar slot presentation. Client-truth data:
        // the worker validates it and falls back to the host calendar
        // timezone when absent or invalid.
        respondentTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // SEC-5: Sensitive/session params sent via first WS message, not in URL (avoids log exposure)
      const wsUrl = `${LIVE_SESSION_WORKER_URL}/ws?room_id=${encodeURIComponent(roomId)}&interview_data_id=${encodeURIComponent(sessionIdentityId)}`;

      /** Start video recording if enabled and stream is available. */
      const startVideoRecording = () => startVideoRecordingIfReady();

      const MAX_RECONNECT_ATTEMPTS = 3;

      /**
       * Build the React-coupled message handlers for a parsed server frame.
       * The SessionConnection owns the socket and reconnect machine; these
       * handlers drive React state and send replies through the connection.
       */
      const buildMessageHandlers = (conn: SessionConnection): MessageHandlers => ({
        onConversationState: state => conversationStateHandler.current?.(state),
        onReady: (logoUrl) => {
          if (logoUrl) setBrandLogoUrl(logoUrl);
          setStatus('Sending context...');
          // Forward browser PostHog IDs to the worker so backend captures can
          // reuse the same distinct/session identity for cross-runtime linking.
          conn.send({ type: 'context', context: interviewContext, posthog: getPostHogIds() });
        },
        onPing: (timestamp) => {
          // Echo the ping's server-clock timestamp and stamp our own clock so
          // the worker can estimate the client-minus-server clock offset. A ping
          // without a timestamp (old worker) still gets a valid bare-ish pong.
          conn.send(
            typeof timestamp === 'number'
              ? { type: 'pong', echo_timestamp: timestamp, client_time_ms: Date.now() }
              : { type: 'pong' },
          );
        },
        onVideoHeartbeatAck: (warning) => {
          setVideoRecordingWarning(warning || null);
        },
        onConnected: () => {
          setStatus('Listening...');
          wsReadyRef.current = true;
          // Mirror the connection's connected gate onto connectedRef so mic
          // auto-recovery (handleTrackEnded) stays armed.
          connectedRef.current = true;
          sessionCallStartedAtRef.current = Date.now();
          trackLiveInterviewEvent('live_interview.session_connected', 'in_call');
          startVideoRecording();
        },
        onInactivityWarning: (countdown) => startInactivityCountdown(countdown),
        onInactivityCanceled: () => cancelInactivityCountdown(),
        onKicked: (reason) => {
          cancelInactivityCountdown();
          setStatus(reason);
          wsReadyRef.current = false;
          setTimeout(() => {
            cleanupResources();
            window.location.href = '/';
          }, 1500);
        },
        onError: (code, terminal, retryable) => {
          const message = safeServerErrorMessage(code);
          if (terminal) {
            showFatalConnectionError(message, code, retryable);
            return;
          }
          setStatus(message);
        },
        onSessionDurationWarning: (remainingSeconds) => {
          console.log(`[Session] Will refresh in ${remainingSeconds}s for continuity`);
        },
        onSessionRefreshed: () => {
          console.log('[Session] Refreshed for continuity');
          wsReadyRef.current = true;
          setStatus('Listening...');
          // Proactive ~10min make-before-break refresh: the worker resumes the
          // AI via 'Continue.' on the recreated upstream session. Re-arm first-utterance
          // protection so that resumed speech is shielded from the mic (otherwise
          // the reconnect self-hearing recurs on every refresh, since protection
          // has long since lifted by this point).
          armFirstUtteranceProtection();
        },
        onReconnecting: (attempt, maxAttempts) => {
          setStatus(`Reconnecting... (${attempt}/${maxAttempts})`);
        },
        onReconnected: (upstreamSession, microphoneGate) => {
          wsReadyRef.current = true;
          // Restore connectedRef: an onerror/onclose cleared it during the
          // drop, which would otherwise permanently disable mic auto-recovery
          // (useAudioPipeline.handleTrackEnded) for the rest of the call.
          connectedRef.current = true;
          setStatus('Listening...');
          console.log('[Session] Reconnected successfully', { upstreamSession, microphoneGate });
          startVideoRecording();
          // Re-arm first-utterance protection so the AI's first resumed speech
          // after this reconnect is shielded from the mic (preventing the
          // self-hearing / VAD self-interrupt) while still allowing barge-in
          // once that utterance finishes. Applies to both reconnect flavors:
          // both preserved and recreated upstream sessions.
          armFirstUtteranceProtection();
          // A preserved idle upstream cannot emit another audio chunk merely
          // because the browser rejoined. The server explicitly tells us when
          // the gate may open without waiting for assistant output.
          if (microphoneGate === 'open_immediately') {
            markAiIntroStarted();
          }
        },
        onDisconnected: () => {
          setStatus('Connection closed');
          wsReadyRef.current = false;
        },
        onInterviewEnding: () => {
          setQuestionsComplete(true);
          confirmEndingAfterPlayback();
        },
        onInterviewEnded: () => {
          clearEndingCountdown();
          handleEndCallAndCleanup(false);
        },
        onRealtimeSessionReady: (microphoneSampleRateHz, preferredChunkDurationMs) => {
          setRealtimeAudioPolicy(microphoneSampleRateHz, preferredChunkDurationMs);
        },
        onRealtimeTranscript: (speaker, text, final, itemId) => {
          void playAudioChunk(
            { type: 'realtime_transcript', speaker, text, final, itemId },
            analyserNode,
            status,
            setStatus,
            setConversation,
            setQuestionsComplete,
            onPerformResearch,
          );
        },
        onRealtimeAudio: async (data, sampleRateHz, itemId, contentIndex, providerGeneration) => {
          await playAudioChunk(
            { type: 'realtime_audio', data, sampleRateHz, itemId, contentIndex,
              ...(providerGeneration !== undefined ? { providerGeneration } : {}) },
            analyserNode,
            status,
            setStatus,
            setConversation,
            setQuestionsComplete,
            onPerformResearch,
          );
        },
        onRealtimeResponseState: (state, itemId) => {
          void playAudioChunk(
            { type: 'realtime_response_state', state, itemId },
            analyserNode,
            status,
            setStatus,
            setConversation,
            setQuestionsComplete,
            onPerformResearch,
          );
        },
        onRealtimeInterrupted: (itemId) => {
          void playAudioChunk(
            { type: 'realtime_interrupted', itemId },
            analyserNode,
            status,
            setStatus,
            setConversation,
            setQuestionsComplete,
            onPerformResearch,
          );
        },
        onRealtimeToolState: (
          callId,
          name,
          state,
          timestamp,
          responseItemId,
          args,
          result,
        ) => {
          void playAudioChunk(
            {
              type: 'realtime_tool_state',
              callId,
              name,
              state,
              timestamp,
              responseItemId,
              arguments: args,
              result,
            },
            analyserNode,
            status,
            setStatus,
            setConversation,
            setQuestionsComplete,
            onPerformResearch,
          );
        },
        onScreenShareRequested: (reason) => {
          screenShareRequestFromAi(reason);
        },
        onScreenShareEndRequested: (reason) => {
          screenShareOnEndRequestedFromAi(reason);
        },
        onRestartRecording: () => {
          // Reconnect segment boundary (DEFECT 2): a SURVIVING continuous
          // recorder must restart so the next segment opens on a header-bearing
          // chunk 0. If no recorder survives (page reload), a fresh recorder
          // already starts at chunk 0, so ignore the signal.
          const recorder = videoRecorderRef.current;
          const ws = wsRef.current;
          if (!recorder || !ws || ws.readyState !== WebSocket.OPEN) return;
          void recorder.restart(ws).catch((error) => {
            captureException(
              error instanceof Error ? error : String(error),
              'UseSessionWebSocket.onRestartRecording',
              { roomId },
            );
          });
        },
        onMountInformationBlock: (fieldName, imageUrl, title, metadata) => {
          mountInformationBlock(fieldName, imageUrl, title, metadata);
        },
        onUnmountInformationBlock: (fieldName) => {
          unmountInformationBlock(fieldName);
        },
        onUnknown: (type, _data) => {
          console.warn(`[Session] Unknown message type: ${type}`);
        },
      });

      /** Map a fatal close code to its user-facing message. */
      const fatalMessageForCode = (code: number | null, _reason?: string): string => {
        switch (code) {
          case 4001:
            return 'Invalid credentials';
          case 4002:
            return safeServerErrorMessage('conversation_unavailable');
          case 4003:
            return safeServerErrorMessage('service_unavailable');
          case 4004:
            return safeServerErrorMessage('realtime_unavailable');
          default:
            return 'Connection lost. Please check your internet connection.';
        }
      };

      const conn = new SessionConnection({
        wsUrl,
        auth: { interviewDataId, passcode, enforceLanguage: enforceLanguage || null, responseToken, participationMode: 'call' },
        maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
        events: {
          onStateChange: (state) => {
            setConnectionState(state);
            // Keep wsRef pointed at the live socket for consumers and the
            // teardown close path; the class creates a new socket on each
            // Connecting/Reconnecting transition.
            wsRef.current = conn.socket;
            if (state === ConnectionState.Reconnecting) {
              // The old socket is gone until the handshake completes; gate the
              // audio pipeline until onReconnected/onConnected restores it.
              wsReadyRef.current = false;
              setStatus(`Reconnecting... (${conn.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);
            } else if (state === ConnectionState.Connecting) {
              setStatus('Connecting to AI...');
            }
          },
          onServerMessage: (wsData) => {
            if (hasEndedRef.current || fatalConnectionErrorShownRef.current) return;
            const handlers = buildMessageHandlers(conn);
            // The connection only forwards successfully JSON.parsed frames;
            // routeMessage defensively handles a missing/non-string `type`.
            Promise.resolve(routeMessage(wsData as Record<string, unknown>, handlers)).catch((e) => {
              console.error('WebSocket message error:', e);
              captureException(e instanceof Error ? e : String(e), 'UseSessionWebSocket.handleServerMessage', {
                roomId,
                intervieweeName: context.intervieweeName,
                targetName: context.targetName,
              });
            });
          },
          onError: (error) => {
            console.error('WebSocket error:', error);
            captureException('WebSocket connection error', 'UseSessionWebSocket.handleConnectionError', {
              roomId,
              wasConnected: wsReadyRef.current,
              intervieweeName: context.intervieweeName,
              targetName: context.targetName,
              interviewLanguage: context.interviewLanguage,
            });
            wsReadyRef.current = false;
            connectedRef.current = false;
          },
          onFatal: ({ code, reason }) => {
            wsReadyRef.current = false;
            connectedRef.current = false;
            if (!hasEndedRef.current) {
              showFatalConnectionError(
                fatalMessageForCode(code, reason),
                code === 4002
                  ? 'conversation_unavailable'
                  : code === 4003
                    ? 'service_unavailable'
                    : code === 4004
                      ? 'realtime_unavailable'
                    : null,
                code !== 4002 && code !== 4004,
              );
            }
          },
          onClose: ({ code, wasConnected }) => {
            wsReadyRef.current = false;
            connectedRef.current = false;
            if (hasEndedRef.current) return;

            // Pre-Connected 1006: disambiguate an occupancy conflict from a
            // generic network failure before surfacing a fatal error.
            if (code === 1006 && !wasConnected) {
              void (async () => {
                try {
                  const checkUrl = `${LIVE_SESSION_WORKER_URL}/check-occupancy?interview_data_id=${encodeURIComponent(sessionIdentityId)}`;
                  const checkResponse = await fetch(checkUrl);
                  if (checkResponse.ok) {
                    const checkData = await checkResponse.json() as { occupied?: boolean };
                    if (checkData.occupied) {
                      showFatalConnectionError('This interview is already open elsewhere. Please close the other session and try again.');
                      return;
                    }
                  }
                } catch (_e) {
                  // Ignore check errors; fall back to the generic message.
                }
                showFatalConnectionError('Connection failed. Please check your internet connection and try again.');
              })();
              return;
            }

            // Clean close: just reflect it in the status.
            setStatus('Connection closed.');
          },
        },
      });

      sessionConnectionRef.current = conn;
      conn.connect();
      wsRef.current = conn.socket;

    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        connectedRef.current = false;
        return;
      }
      console.error("Failed to start session:", error);
      captureException(error instanceof Error ? error : String(error), 'UseSessionWebSocket.startSession', {
        roomId,
        intervieweeName: context.intervieweeName,
        targetName: context.targetName,
        interviewLanguage: context.interviewLanguage,
      });
      connectedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleEndCallAndCleanup, onPerformResearch, context, cleanupResources, roomId, passcode, setupAudioPipeline, playAudioChunk, confirmEndingAfterPlayback, cancelInactivityCountdown, startInactivityCountdown, clearEndingCountdown, sendToWorker, showFatalConnectionError, wsRef, wsReadyRef, videoEnabled, interviewDataId, enforceLanguage, sessionIdentityId, trackLiveInterviewEvent, responseToken]);

  const setShowReportMenuTracked = useCallback((value: boolean | ((previous: boolean) => boolean)) => {
    setShowReportMenu((previous) => {
      const next = typeof value === 'function' ? value(previous) : value;
      if (next !== previous) {
        trackLiveInterviewEvent('live_interview.control_more_menu_toggled', 'in_call', {
          state: next ? 'open' : 'closed',
        });
      }
      return next;
    });
  }, [trackLiveInterviewEvent]);

  return {
    // State
    conversation,
    mountedInformationBlock,
    status,
    connectionState,
    showEndModal,
    questionsComplete,
    hasConnectionError,
    connectionErrorMessage,
    connectionErrorCode,
    connectionErrorRetryable,
    isVideoRecording,
    isVideoPaused,
    videoRecordingWarning,
    isVideoPreviewMinimized,
    showReportMenu,
    showReportModal,
    reportIssueText,
    isNudging,
    brandLogoUrl,
    // Refs
    videoPreviewRef,
    reportMenuRef,
    // Actions
    startSession,
    cleanupResources,
    requestEndInterview,
    handleConfirmEnd,
    handleCancelEnd,
    handleNudgeAi,
    handleReportIssue,
    toggleVideoPause,
    // State setters
    setStatus,
    setShowReportMenu: setShowReportMenuTracked,
    setShowReportModal,
    setReportIssueText,
    setIsVideoPreviewMinimized,
    setQuestionsComplete,
  };
}
